"use client";

import { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { DayPicker, type Matcher } from "react-day-picker";
import { format } from "date-fns";
import { lt } from "date-fns/locale/lt";
import { initialVisibleMonth } from "@/lib/calendar";
import "react-day-picker/style.css";

interface DatePopoverProps {
  value: Date | undefined;
  onSelect: (date: Date | undefined) => void;
  disabled?: Matcher | Matcher[];
  placeholder?: string;
  ariaLabel?: string;
  /** Month to show when the calendar opens (e.g. the selected date, or the pickup date for the return field). */
  defaultMonth?: Date;
}

export default function DatePopover({
  value,
  onSelect,
  disabled,
  placeholder = "Pasirinkite",
  ariaLabel = "Atidaryti kalendorių",
  defaultMonth,
}: DatePopoverProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className="flex-1 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          <svg className="w-4 h-4 text-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className={value ? "text-gray-900" : "text-gray-400"}>
            {value ? format(value, "yyyy-MM-dd") : placeholder}
          </span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          collisionPadding={8}
          className="z-50 bg-white rounded-xl shadow-2xl border border-gray-200 p-3 max-w-[calc(100vw-1rem)]"
        >
          <DayPicker
            mode="single"
            selected={value}
            defaultMonth={initialVisibleMonth(value, defaultMonth)}
            onSelect={(d) => {
              onSelect(d);
              setOpen(false);
            }}
            locale={lt}
            numberOfMonths={1}
            disabled={disabled}
            showOutsideDays
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
